/* Modenaire homepage sections (Oct 2026), ported from the press page (assets/press-page.js): the opening films,
   the curved 3D gallery of the edits, the mood film, the new arrivals spotlight and the sideways About slide.
   Each is a custom element built on MD.Section (md-core.js), so the theme editor can reload any of them cleanly.
   The HTML is all rendered by Shopify; this file only adds the motion. */
(() => {
  const MD = window.MD;
  if (!MD || window.MD_HOME) return;
  window.MD_HOME = true;
  const { clamp, mod, reduce } = MD;
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 600));
  const afterLoad = (host, fn) => { if (document.readyState === 'complete') idle(fn); else host.on(window, 'load', () => idle(fn), { once: true }); };
  const tryPlay = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
  const pausedPref = () => { try { return sessionStorage.getItem('md-film-paused') === '1'; } catch (e) { return false; } };
  const savePaused = (p) => { try { sessionStorage.setItem('md-film-paused', p ? '1' : '0'); } catch (e) { /* private mode */ } };
  const syncPause = (btn, paused) => {
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(paused));
    btn.setAttribute('aria-label', paused ? btn.dataset.play : btn.dataset.pause);
    const a = btn.querySelector('.is-pause'), b = btn.querySelector('.is-play');
    if (a) a.hidden = paused; if (b) b.hidden = !paused;
  };

  // ---------- 1. opening: three films side by side, one on upright screens ----------
  class Hero extends MD.Section {
    setup() {
      this.mq = matchMedia('(max-aspect-ratio: 4/5)');
      this.vids = [...this.querySelectorAll('.md-hero__film video')];
      this.oneAtATime = this.dataset.play !== 'all';   // wide screens: one film plays while the other two hold their cover photo
      this.turn = Math.max(0, this.vids.findIndex((v) => v.parentElement.classList.contains('is-phone')));
      this.btn = this.querySelector('.md-hero__pause');
      this.paused = pausedPref();
      this.on(this.querySelector('.md-hero__explore'), 'click', (e) => {
        const next = (this.closest('.shopify-section') || this).nextElementSibling;
        if (!next) return;
        e.preventDefault();
        scrollTo({ top: next.getBoundingClientRect().top + scrollY, behavior: reduce.matches ? 'auto' : 'smooth' });
      });
      const still = reduce.matches || MD.lite() || !this.vids.length;
      if (this.btn) this.btn.hidden = still;
      if (still) return;   // the cover photos show instead
      this.on(this.btn, 'click', () => this.setPaused(!this.paused));
      this.on(this.mq, 'change', () => this.load());
      this.on(document, 'visibilitychange', () => { if (document.visibilityState === 'visible') this.playVisible(); });
      this.vids.forEach((v, i) => {
        v.muted = true; v.defaultMuted = true; v.playsInline = true; v.loop = false;
        MD.playWhenVisible(this, v, () => this.mayPlay(i));
        this.on(v, 'playing', () => v.parentElement.classList.add('is-playing'));
        // at the end a film fades to its cover photo; then it starts again, or the next film takes its turn
        this.on(v, 'ended', () => {
          v.parentElement.classList.remove('is-playing');
          this.timers.push(setTimeout(() => {
            if (this.paused || !this.inView()) return;
            if (this.oneAtATime && !this.mq.matches) this.turn = (i + 1) % this.vids.length;
            const next = this.vids[this.oneAtATime && !this.mq.matches ? this.turn : i];
            next.currentTime = 0; tryPlay(next);
          }, 900));
        });
      });
      syncPause(this.btn, this.paused);
      afterLoad(this, () => this.load());   // films never compete with the page's first paint
    }
    load() {
      const phone = this.mq.matches;
      this.vids.forEach((v) => {
        const fig = v.parentElement, isPhone = fig.classList.contains('is-phone');
        const want = phone ? (isPhone ? v.dataset.srcPhone || v.dataset.src : '') : v.dataset.src;
        if (!want) { v.pause(); return; }
        if (v.dataset.cur === want) return;
        v.dataset.cur = want; fig.classList.remove('is-playing');
        v.src = want; v.load();
        if (!this.paused && this.inView() && this.mayPlay(this.vids.indexOf(v))) tryPlay(v);
      });
    }
    inView() { const r = this.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight; }
    mayPlay(i) { return this.mq.matches || !this.oneAtATime || i === this.turn; }
    playVisible() { if (this.paused || !this.inView()) return; this.vids.forEach((v, i) => { if (v.dataset.cur && this.mayPlay(i)) tryPlay(v); }); }
    setPaused(p) { this.paused = p; savePaused(p); if (p) this.vids.forEach((v) => v.pause()); else this.playVisible(); syncPause(this.btn, p); }
    teardown() { (this.vids || []).forEach((v) => { v.pause(); v.removeAttribute('src'); delete v.dataset.cur; }); }
  }
  MD.define('md-hero', Hero);

  // ---------- 4. the edits: a curved gallery drawn with WebGL (three.js r128), on a loop ----------
  const THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  const VS = `
    uniform float uBend; uniform float uShift;
    varying vec2 vUv; varying float vX;
    void main() {
      vUv = uv;
      vec4 w = modelMatrix * vec4(position, 1.0);
      w.z += w.x * w.x * uBend;
      w.y += w.x * uShift;
      vX = w.x;
      gl_Position = projectionMatrix * viewMatrix * w;
    }`;
  const FS = `
    uniform sampler2D uTex; uniform vec2 uScale; uniform float uReady; uniform float uFar;
    varying vec2 vUv; varying float vX;
    void main() {
      vec2 uv = vec2((vUv.x - 0.5) * uScale.x + 0.5, (vUv.y - 1.0) * uScale.y + 1.0);
      vec3 c = texture2D(uTex, uv).rgb;
      float near = 1.0 - smoothstep(0.0, uFar, abs(vX));
      vec3 bg = vec3(0.227, 0.173, 0.176);
      gl_FragColor = vec4(mix(bg, c * (0.42 + 0.58 * near), uReady), 1.0);
    }`;
  class Gallery extends MD.Section {
    setup() {
      this.pin = this.querySelector('.md-gal__pin');
      this.cv = this.querySelector('.md-gal__gl');
      this.capBody = this.querySelector('.md-gal__capbody');
      this.live = this.querySelector('.md-gal__live');
      this.list = this.querySelector('.md-gal__list');
      this.allLink = this.querySelector('.md-gal__all');
      this.countEl = this.querySelector('.md-gal__count span');
      this.ringEl = this.querySelector('.md-gal__count circle + circle');
      this.tabs = [...this.querySelectorAll('.md-gal__edit')];
      this.editId = this.list ? this.list.dataset.edit : '';
      this.cache = new Map(); this.pending = {};
      if (this.list) this.cache.set(this.editId, [...this.list.querySelectorAll(':scope > .md-card')]);
      this.cur = 0; this.target = 0; this.rest = 0; this.vel = 0; this.active = -1; this.meshes = []; this.lap = 0; this.gl = false;
      const ws = parseInt(this.dataset.wheel, 10);
      this.wheelSteps = Number.isNaN(ws) ? 8 : ws;
      this.useAlt = this.dataset.image === 'second';
      this.short = this.dataset.short === 'true';   // long live titles ('... with Collared Neck and Patch Pockets') are cut at 'with' or 'for' in the caption
      MD.drawRibbons(this); MD.reveal(this);
      this.tabs.forEach((t) => {
        this.on(t, 'click', (e) => { e.preventDefault(); this.setEdit(t.dataset.edit); });
        this.on(t, 'pointerenter', () => this.fetchEdit(t.dataset.edit), { once: true });
      });
      this.querySelectorAll('[data-step]').forEach((b) => this.on(b, 'click', () => this.step(Number(b.dataset.step))));
      this.on(this, 'keydown', (e) => {
        if (!this.gl || e.target.closest('input')) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.step(e.key === 'ArrowRight' ? 1 : -1); }
      });
      this.items = this.readItems();
      // the 3D version needs WebGL and enough memory, and is skipped for anyone who asks for less motion
      const canGL = !reduce.matches && this.webgl() && !(navigator.deviceMemory && navigator.deviceMemory <= 2) && 'IntersectionObserver' in window;
      if (this.items.length) this.io((es) => { if (es[0].isIntersecting) this.tabs.forEach((t) => this.fetchEdit(t.dataset.edit)); }, { rootMargin: '60% 0px' }).observe(this);
      if (!canGL || !this.items.length) return;
      this.io((es) => {
        if (!es[0].isIntersecting || this.loading3) return;
        this.loading3 = true;
        MD.loadScript(THREE_URL).then(() => this.startGL()).catch(() => {});
      }, { rootMargin: '150% 0px' }).observe(this);
    }
    webgl() { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl') || c.getContext('experimental-webgl')); } catch (e) { return false; } }
    readItems() {
      if (!this.list) return [];
      return [...this.list.querySelectorAll(':scope > .md-card')].map((el) => el.dataset.more
        ? { el, url: el.dataset.url, more: true, name: el.dataset.name || '', title: this.dataset.more || 'View more', price: '' }   // the last tile: the whole collection
        : {
          el, url: el.dataset.url,
          tex: (this.useAlt && el.dataset.texAlt) || el.dataset.tex,
          texSm: (this.useAlt && el.dataset.texAltSm) || el.dataset.texSm,
          title: this.shortName((el.querySelector('.md-card__title') || el).textContent.trim()),
          price: ((el.querySelector('.price__current') || {}).textContent || '').replace(/\s+/g, ' ').trim(),
        }).filter((it) => it.tex || it.more);
    }
    // the View more tile, drawn in the page's own colours and fonts (redrawn once the web fonts are in)
    moreCanvas(it, tex) {
      const c = document.createElement('canvas'), W = 600, H = 800; c.width = W; c.height = H;
      const draw = () => {
        const x = c.getContext('2d');
        x.fillStyle = '#EFE9DF'; x.fillRect(0, 0, W, H);
        x.strokeStyle = '#D9CFC1'; x.lineWidth = 2; x.strokeRect(24, 24, W - 48, H - 48);
        x.fillStyle = '#2B1F20'; x.textAlign = 'center'; x.textBaseline = 'middle';
        try { x.letterSpacing = '5px'; } catch (e) { /* older browsers */ }
        x.font = '500 22px Inter, system-ui, sans-serif'; x.fillStyle = '#75625F'; x.fillText(it.name.toUpperCase(), W / 2, H / 2 - 92);
        try { x.letterSpacing = '2px'; } catch (e) { /* fine */ }
        x.font = '300 80px "Cormorant Garamond", Georgia, serif'; x.fillStyle = '#2B1F20'; x.fillText(it.title.toUpperCase(), W / 2, H / 2 + 4);
        x.strokeStyle = '#2B1F20'; x.lineWidth = 2.5; x.beginPath(); x.moveTo(W / 2 - 34, H / 2 + 90); x.lineTo(W / 2 + 34, H / 2 + 90); x.moveTo(W / 2 + 20, H / 2 + 78); x.lineTo(W / 2 + 34, H / 2 + 90); x.lineTo(W / 2 + 20, H / 2 + 102); x.stroke();
        if (tex) tex.needsUpdate = true;
      };
      draw();
      if (document.fonts && document.fonts.load) Promise.all([document.fonts.load('300 80px "Cormorant Garamond"'), document.fonts.load('500 22px Inter')]).then(draw).catch(() => {});
      return c;
    }
    shortName(t) {
      if (!this.short) return t;
      const s = t.replace(/\s+(with|for)\s+[\s\S]*$/i, '').trim();
      return s.length >= 12 ? s : t;   // never cut a name down to almost nothing
    }
    startGL() {
      if (!this.isConnected || !window.THREE || this.gl) return;
      const T = window.THREE;
      try { this.renderer = new T.WebGLRenderer({ canvas: this.cv, antialias: true, alpha: true }); } catch (e) { return; }
      this.gl = true;
      this.classList.add('is-gl');
      if (this.list) this.list.inert = true;   // the caption shows the piece in front; the hidden list mustn't take focus
      this.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2)); this.renderer.setClearColor(0x000000, 0);
      this.scene = new T.Scene(); this.camera = new T.PerspectiveCamera(35, 1, 0.1, 100); this.camera.position.z = 10;
      this.geo = new T.PlaneGeometry(1, 1, 40, 1);
      this.U = { bend: { value: 0.06 }, shift: { value: 0 }, far: { value: 6 } };
      this.loader = new T.TextureLoader(); this.loader.setCrossOrigin('anonymous');
      this.buildMeshes(); this.caption(); this.layout();
      if (document.fonts) document.fonts.ready.then(() => this.layout());   // the title's height depends on the web fonts
      this.io((es) => {
        this.visible = es[0].isIntersecting;
        if (!this.visible) this.lap = null;
        if (this.visible && !this.raf) this.raf = requestAnimationFrame(() => this.tick());
      }, { rootMargin: '100px 0px' }).observe(this);
      this.wLast = 0; this.wStep = 0; this.wAcc = 0; this.wUsed = false;
      if (!MD.design) this.on(this.pin, 'wheel', (e) => this.onWheel(e), { passive: false });
      this.on(window, 'resize', () => this.layout());
      // swipe or drag sideways on the photos to browse; up-and-down swipes stay with the page (touch-action: pan-y)
      const per = () => (this.SP / this.viewW) * this.cv.clientWidth;
      this.on(this.cv, 'pointerdown', (e) => { if (!e.button) this.drag = { id: e.pointerId, x: e.clientX, from: this.target, moved: false, lx: e.clientX, lt: performance.now(), v: 0 }; });
      this.on(this.cv, 'pointermove', (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        const dx = e.clientX - d.x;
        if (!d.moved && Math.abs(dx) > 8) { d.moved = true; try { this.cv.setPointerCapture(e.pointerId); } catch (_) { /* fine */ } }
        if (!d.moved) return;
        this.target = d.from - dx / per();
        const now = performance.now(); d.v = (e.clientX - d.lx) / Math.max(1, now - d.lt); d.lx = e.clientX; d.lt = now;
      });
      const endDrag = (e) => {
        const d = this.drag;
        if (!d || e.pointerId !== d.id) return;
        if (d.moved) { this.rest = Math.round(this.target - clamp((d.v * 160) / per(), -3, 3)); this.target = this.rest; this.dragEnd = performance.now(); }
        this.drag = null;
        this.announce = true;
      };
      this.on(this.cv, 'pointerup', endDrag); this.on(this.cv, 'pointercancel', endDrag);
      // a tap on the piece in front opens it; a tap on a piece to the side brings it to the front
      this.on(this.cv, 'click', (e) => {
        if (performance.now() - (this.dragEnd || 0) < 350) return;
        const r = this.cv.getBoundingClientRect(), x = ((e.clientX - r.left) / r.width - 0.5) * this.viewW;
        let best = null, bd = Infinity;
        this.meshes.forEach((m) => { const d = Math.abs(m.position.x - x); if (d < bd) { bd = d; best = m; } });
        if (!best || bd > this.PW * 0.6) return;
        const slot = Math.round(best.position.x / this.SP);
        if (slot === 0) { const it = this.items[this.active]; if (it && it.url) location.href = it.url; }
        else this.step(slot);
      });
    }
    buildMeshes() {
      const T = window.THREE, old = new Set();
      this.meshes.forEach((m) => { this.scene.remove(m); if (m.material.uniforms.uTex.value) old.add(m.material.uniforms.uTex.value); m.material.dispose(); });
      old.forEach((t) => t.dispose());
      // the ring of places round the band. With a View more tile, the pieces go round twice with the tile between
      // the two laps, so it sits half a lap away and only comes round once the shopper browses; a short edit is
      // repeated so both sides are always filled
      const n = this.items.length, small = innerWidth < 768;
      const prods = this.items.map((it, i) => i).filter((i) => !this.items[i].more), moreIdx = this.items.findIndex((it) => it.more);
      let ring = moreIdx >= 0 && prods.length ? [...prods, moreIdx, ...prods] : this.items.map((_, i) => i);
      while (ring.length && ring.length < 7) ring = ring.concat(ring);
      this.ring = ring;
      const make = () => {
        const u = { uTex: { value: null }, uScale: { value: new T.Vector2(1, 1) }, uReady: { value: 0 }, uBend: this.U.bend, uShift: this.U.shift, uFar: this.U.far };
        const m = new T.Mesh(this.geo, new T.ShaderMaterial({ vertexShader: VS, fragmentShader: FS, uniforms: u }));
        m.userData.want = 0; this.scene.add(m); return m;
      };
      const perItem = this.items.map(() => []);
      this.meshes = ring.map((idx) => { const m = make(); perItem[idx].push(m); return m; });   // slot j shows piece ring[j]
      this.items.forEach((it, i) => {
        const ms = perItem[i];
        if (it.more) {
          const tex = new T.CanvasTexture(this.moreCanvas(it, null)); tex.minFilter = T.LinearFilter; tex.generateMipmaps = false;
          this.moreCanvas(it, tex);
          ms.forEach((m) => { m.material.uniforms.uTex.value = tex; m.userData.want = 1; });
          return;
        }
        this.loader.load((small && it.texSm) || it.tex, (tex) => {
          if (!this.gl) { tex.dispose(); return; }
          tex.minFilter = T.LinearFilter; tex.generateMipmaps = false;
          const ia = tex.image.width / tex.image.height, pa = 0.75;
          ms.forEach((m) => { const u = m.material.uniforms; u.uScale.value.set(ia > pa ? pa / ia : 1, ia > pa ? 1 : ia / pa); u.uTex.value = tex; m.userData.want = 1; });
        });
      });
      this.cur = this.target = this.rest = 0; this.active = -1; this.lap = 0;
    }
    layout() {
      if (!this.gl) return;
      const w = this.pin.clientWidth, h = this.pin.clientHeight, land = w > h && h < 560, phone = w < 768 && !land;
      this.renderer.setSize(w, h, false); this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
      const viewH = 2 * this.camera.position.z * Math.tan((Math.PI / 180) * this.camera.fov / 2);
      this.viewW = viewH * this.camera.aspect;
      this.k = (phone ? 0.5 : 2.1) / Math.pow(this.viewW / 2, 2);
      this.U.far.value = this.viewW * (phone ? 0.95 : 0.55);
      // the edit names go just under the title, and the photo is sized to the room between them and the caption
      const side = this.querySelector('.md-gal__side'), cap = this.querySelector('.md-gal__cap');
      side.style.top = '';
      const pt = this.pin.getBoundingClientRect().top, hb = this.querySelector('.md-gal__head h2').getBoundingClientRect().bottom - pt, gap = phone ? 14 : 24;
      if (!land) side.style.top = Math.round(hb + (phone ? 10 : 18)) + 'px';
      const top = (land || !side.offsetHeight ? hb : side.getBoundingClientRect().bottom - pt) + gap;   // no edit names (a single edit): straight under the heading
      const bot = cap.getBoundingClientRect().top - pt - gap;
      // on phones the photo is at most 60% of the screen wide, so the pieces either side peek in at the edges
      const ph = Math.min(clamp(bot - top, h * 0.2, h * (phone ? 0.46 : 0.5)), phone ? (w * 0.6) / 0.75 : Infinity);
      this.PH = (viewH * ph) / h; this.PW = this.PH * 0.75; this.SP = this.PW * (phone ? 1.06 : 1.24);
      const mid = (top + bot) / 2 / h;
      this.meshes.forEach((m) => { m.scale.set(this.PW, this.PH, 1); m.position.y = (0.5 - mid) * viewH; });
      this.pin.style.setProperty('--arrow-y', Math.round(mid * h) + 'px');
      const half = this.PH / viewH / 2;
      this.band = [mid - half - 0.05, mid + half + 0.05];
    }
    onWheel(e) {
      const n = this.items.length, lapLen = Math.min(n, this.wheelSteps);
      if (!this.gl || !n || lapLen < 1) return;
      const r = this.pin.getBoundingClientRect(), y = (e.clientY - r.top) / r.height, top = r.top;
      if (y < this.band[0] || y > this.band[1] || Math.abs(top) > r.height * 0.35) return;   // not over the photos: the page scrolls on
      const side = Math.abs(e.deltaX) > Math.abs(e.deltaY), d = side ? e.deltaX : e.deltaY;
      if (!d) return;
      const now = performance.now(), dir = Math.sign(d);
      if (now - this.wLast > 180) { this.wAcc = 0; this.wUsed = false; this.endAt = 0; }   // a new swipe
      this.wLast = now;
      // scrolling down starts a lap at the first piece, scrolling up starts at the end to go round backwards; the lap is
      // cleared when the gallery leaves the screen, so a finished lap can't catch the page again
      if (this.lap == null) this.lap = dir > 0 ? 0 : lapLen;
      const atEnd = n < 2 || (!side && (dir > 0 ? this.lap >= lapLen : this.lap <= 0));
      if (atEnd && !this.wUsed) return;   // a new swipe at the end carries on down (or up) the page
      if (atEnd) { if (!this.endAt) this.endAt = now; if (now - this.endAt > 350) return; }   // never hold a long, unbroken scroll
      e.preventDefault();
      if (Math.abs(top) > 2) scrollTo({ top: scrollY + top, behavior: 'smooth' });   // settle the gallery into place
      if (atEnd) return;   // the tail of the swipe that reached the end is absorbed, so it doesn't fling the page
      this.wAcc += d;
      if ((!this.wUsed || now - this.wStep > 650) && Math.abs(this.wAcc) >= 24) {
        this.wUsed = true; this.wStep = now; this.wAcc = 0; this.rest += dir; this.target = this.rest;
        if (!side) this.lap += dir;
      }
    }
    tick() {
      if (!this.visible || !this.gl) { this.raf = 0; return; }
      const n = this.items.length, prev = this.cur;
      this.cur += (this.target - this.cur) * (this.drag ? 0.3 : 0.055);   // follows a finger closely, eases otherwise
      if (Math.abs(this.target - this.cur) < 0.0005) this.cur = this.target;
      this.vel += (this.cur - prev - this.vel) * 0.2;
      this.U.bend.value = this.k * (1 + Math.min(Math.abs(this.vel) * 12, 1.1));
      this.U.shift.value = clamp(this.vel * 0.35, -0.06, 0.06);
      const N = this.meshes.length;   // each photo sits at its nearest place round the loop
      this.meshes.forEach((m, j) => { m.position.x = (mod(j - this.cur + N / 2, N) - N / 2) * this.SP; const u = m.material.uniforms.uReady; u.value += (m.userData.want - u.value) * 0.06; });
      const ring = this.ring || [], a = ring.length ? ring[mod(Math.round(this.cur), ring.length)] : 0;
      if (a !== this.active) { this.active = a; this.caption(); }
      if (this.ringEl) this.ringEl.style.strokeDashoffset = String(1 - (n > 1 ? clamp((this.active + 1) / n, 0, 1) : 1));
      this.renderer.render(this.scene, this.camera);
      this.raf = requestAnimationFrame(() => this.tick());
    }
    caption() {
      const n = this.items.length, it = this.items[clamp(this.active, 0, Math.max(0, n - 1))];
      if (!it || !this.capBody) return;
      const body = it.el.querySelector('.md-card__body').cloneNode(true);
      body.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id'));
      const nm = body.querySelector('.md-card__title a, .md-card__title');
      if (nm) nm.textContent = it.title;   // the shortened name; the link still opens the product
      this.capBody.replaceChildren(body);
      if (this.live && this.announce) this.live.textContent = `${it.title}, ${it.price}`;   // only once the shopper starts browsing
      if (this.countEl) this.countEl.textContent = `${Math.max(1, this.active + 1)} / ${n}`;
    }
    step(d) {
      if (!this.items.length || !this.gl) return;
      this.announce = true;
      this.rest += d; this.target = this.rest; this.settle();
    }
    settle() {
      const y = this.getBoundingClientRect().top + scrollY;
      if (Math.abs(scrollY - y) > 2 && Math.abs(scrollY - y) < innerHeight * 0.6) scrollTo({ top: y, behavior: reduce.matches ? 'auto' : 'smooth' });
    }
    fetchEdit(id) {
      if (this.cache.has(id)) return Promise.resolve(this.cache.get(id));
      if (this.pending[id]) return this.pending[id];
      const tab = this.tabs.find((t) => t.dataset.edit === id);
      if (!tab || !tab.dataset.url) return Promise.resolve(null);
      const u = tab.dataset.url + (tab.dataset.url.includes('?') ? '&' : '?') + 'section_id=md-edit-cards';
      this.pending[id] = fetch(u, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nodes = [...doc.querySelectorAll('.md-gal__list > .md-card')].map((x) => document.importNode(x, true));
          this.cache.set(id, nodes); return nodes;
        })
        .catch(() => { delete this.pending[id]; return null; });
      return this.pending[id];
    }
    async setEdit(id) {
      if (!id || this.editId === id) return;
      this.editId = id; this.announce = true;
      const tab = this.tabs.find((t) => t.dataset.edit === id);
      this.tabs.forEach((t) => { if (t === tab) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      if (this.allLink && tab) { this.allLink.href = tab.href; const nm = this.allLink.querySelector('[data-name]'); if (nm) nm.textContent = tab.textContent.trim(); }
      this.classList.add('is-loading');
      const nodes = await this.fetchEdit(id);
      this.classList.remove('is-loading');
      if (this.editId !== id) return;
      if (!nodes || !nodes.length) { if (tab) location.href = tab.href; return; }   // couldn't load: go to the collection instead
      this.list.dataset.edit = id;
      this.list.replaceChildren(...nodes);
      this.items = this.readItems();
      if (this.gl) { this.buildMeshes(); this.layout(); this.settle(); }
      this.caption();
      if (!this.gl) this.list.scrollTo({ left: 0 });
    }
    onBlockSelect(block) { const t = this.tabs.find((x) => x === block || x.contains(block) || block.contains(x)); if (t) this.setEdit(t.dataset.edit); }
    teardown() {
      this.gl = false; this.loading3 = false;
      this.classList.remove('is-gl');
      if (this.list) this.list.inert = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.renderer) {
        const old = new Set();
        this.meshes.forEach((m) => { if (m.material.uniforms.uTex.value) old.add(m.material.uniforms.uTex.value); m.material.dispose(); });
        old.forEach((t) => t.dispose());
        if (this.geo) this.geo.dispose();
        this.renderer.dispose();
        try { this.renderer.forceContextLoss(); } catch (e) { /* already gone */ }   // repeated editor reloads would run out of contexts
        this.renderer = null;
        if (this.cv) { const fresh = this.cv.cloneNode(false); this.cv.replaceWith(fresh); this.cv = fresh; }   // a lost context can't be reused (theme editor moves)
      }
      this.meshes = [];
    }
  }
  MD.define('md-gallery', Gallery);

  // ---------- 5. mood: a framed film that grows to fill the screen as you scroll ----------
  class Mood extends MD.Section {
    setup() {
      this.v = this.querySelector('video');
      this.box = this.querySelector('.md-mood__frame');
      this.btn = this.querySelector('.md-mood__pause');
      this.paused = pausedPref();
      MD.reveal(this);
      if (reduce.matches || MD.design) this.style.setProperty('--z', '1'); else MD.loop.add(this);
      const still = !this.v || !this.v.dataset.src || reduce.matches || MD.lite();
      if (this.btn) this.btn.hidden = still;
      if (still) return;
      this.v.muted = true; this.v.defaultMuted = true; this.v.playsInline = true;
      this.on(this.v, 'playing', () => this.box.classList.add('is-playing'));
      // the film downloads only once it is about a screen away
      this.io((es) => es.forEach((e) => {
        if (!e.isIntersecting || this.v.dataset.cur) return;
        this.v.dataset.cur = this.v.dataset.src; this.v.src = this.v.dataset.src; this.v.load();
      }), { rootMargin: '100% 0px' }).observe(this);
      MD.playWhenVisible(this, this.v);
      syncPause(this.btn, this.paused);
      this.on(this.btn, 'click', () => {
        this.paused = !this.paused; savePaused(this.paused); syncPause(this.btn, this.paused);
        if (this.paused) this.v.pause(); else tryPlay(this.v);
      });
    }
    frame(vh) {
      // the frame opens over the first screen of scrolling, then holds full screen for the rest (the hold setting)
      const r = this.getBoundingClientRect(), hold = (parseFloat(this.dataset.hold) || 0) / 100 * vh, span = this.offsetHeight - vh - hold;
      this.style.setProperty('--z', span > 0 ? clamp(-r.top / (span * 0.8), 0, 1).toFixed(3) : '1');
    }
    teardown() { if (this.v) { this.v.pause(); this.v.removeAttribute('src'); delete this.v.dataset.cur; } }
  }
  MD.define('md-mood', Mood);

  // ---------- 6. new arrivals spotlight: the heading grows while the section holds; the photo changes beside it ----------
  class Spotlight extends MD.Section {
    setup() {
      MD.drawRibbons(this); MD.reveal(this);
      this.pin = this.querySelector('.md-spot__pin');
      this.pics = [...this.querySelectorAll('.md-spot__pic')];
      this.caps = [...this.querySelectorAll('.md-spot__capitem')];
      this.k = 0;
      const ms = (Number(this.dataset.interval) || 3) * 1000;
      let t = null;
      // the photos stop changing while the pointer or keyboard focus is on them, and for good with the pause button
      const media = this.querySelector('.md-spot__media'), btn = this.querySelector('.md-spot__pause');
      this.held = false; this.stopped = false;
      this.on(media, 'pointerenter', () => { this.held = true; }); this.on(media, 'pointerleave', () => { this.held = false; });
      this.on(this, 'focusin', (e) => { if (media && media.contains(e.target)) this.held = true; });
      this.on(this, 'focusout', (e) => { if (media && !media.contains(e.relatedTarget)) this.held = false; });
      if (btn && this.pics.length > 1 && !reduce.matches) {
        btn.hidden = false; syncPause(btn, false);
        this.on(btn, 'click', () => { this.stopped = !this.stopped; syncPause(btn, this.stopped); });
      }
      const start = () => { t = setInterval(() => this.next(), ms); this.timers.push(t); };
      this.restart = () => { if (t) { clearInterval(t); start(); } };
      if (this.pics.length > 1 && !reduce.matches && 'IntersectionObserver' in window) {
        // the photos wait until the section is a screen away, then all load, so each is ready for its turn
        this.io((es) => { if (es[0].isIntersecting) this.pics.forEach((p) => { const i = p.querySelector('img'); if (i) i.loading = 'eager'; }); }, { rootMargin: '100% 0px' }).observe(this);
        this.io((es) => { const vis = es[0].isIntersecting; if (vis && !t) start(); else if (!vis && t) { clearInterval(t); t = null; } })
          .observe(this.querySelector('.md-spot__media'));
      }
      if (!reduce.matches && !MD.design && this.pin) MD.loop.add(this);
      this.fit(); if (document.fonts) document.fonts.ready.then(() => this.fit());
    }
    measure() { this.fit(); }
    // the heading grows to full size beside the photo; on a wide, short screen its longest line can reach under the
    // photo, so the type is shrunk until the widest line fits the room to the photo's left (phones stack them, so no fit)
    fit() {
      const h = this.querySelector('.md-spot__h'), media = this.querySelector('.md-spot__media');
      if (!h) return;
      h.style.fontSize = '';
      if (!media || getComputedStyle(this).getPropertyValue('--stack').trim() === '1' || reduce.matches || MD.design) return;   // stacked layouts (phones, upright tablets) need no fit
      const sg = parseFloat(this.pin.style.getPropertyValue('--sg')) || 1;   // the heading is drawn scaled, so measured widths are too
      const avail = media.getBoundingClientRect().left - h.getBoundingClientRect().left - 28;
      let widest = 0;
      h.querySelectorAll('.md-gl').forEach((el) => { const r = document.createRange(); r.selectNodeContents(el); widest = Math.max(widest, r.getBoundingClientRect().width / sg); });
      if (avail > 160 && widest > avail) h.style.fontSize = Math.floor(parseFloat(getComputedStyle(h).fontSize) * avail / widest) + 'px';
    }
    next() {
      const n = this.pics.length;
      if (n < 2 || this.held || this.stopped) return;
      this.pics.forEach((p) => p.classList.remove('was'));
      this.pics[this.k].classList.replace('on', 'was');
      if (this.caps[this.k]) this.caps[this.k].hidden = true;
      this.k = (this.k + 1) % n;
      this.pics[this.k].classList.add('on');
      if (this.caps[this.k]) this.caps[this.k].hidden = false;
    }
    frame() {
      const stage = this.pin.firstElementChild, r = this.pin.getBoundingClientRect();
      const hold = (parseFloat(this.dataset.hold) || 0) / 100 * innerHeight, run = this.pin.offsetHeight - stage.offsetHeight - hold;   // grows, then holds
      const q = clamp(-r.top / Math.max(1, run), 0, 1);
      this.pin.style.setProperty('--sg', (0.55 + 0.45 * (1 - Math.pow(1 - q, 2))).toFixed(3));   // eases out as it reaches full size
      if (q > 0.4 && !this.bumped) { this.bumped = true; this.next(); this.restart(); }   // one change while the shopper is here
      if (q === 0) this.bumped = false;
    }
  }
  MD.define('md-spotlight', Spotlight);

  // ---------- 7. about: three screens. Each scroll inside the held section plays one slide, which always finishes ----------
  class About extends MD.Section {
    setup() {
      this.pin = this.querySelector('.md-about__pin');
      this.stage = this.querySelector('.md-about__stage');
      this.panels = [...this.querySelectorAll('.md-about__panel')];
      this.A = { step: 0, last: Math.max(0, this.panels.length - 1), busy: false, acc: 0, y: scrollY, ms: 850, hold: false, wheelAt: 0, touch: 0, used: -1, input: '' };
      if (MD.design || reduce.matches || !this.pin) this.classList.add('is-flat');
      this.light(0);
      MD.reveal(this);
      if (this.classList.contains('is-flat')) return;
      this.fit();
      if (document.fonts) document.fonts.ready.then(() => this.fit());
      MD.loop.add(this);
      // on a mouse or trackpad the wheel is held while a slide runs, so one gesture moves one screen; the (non-passive)
      // wheel listener exists only while the section is near the screen
      this.io((es) => { if (es[0].isIntersecting) this.hookWheel(); else this.unhookWheel(); }, { rootMargin: '30% 0px' }).observe(this);
      this.on(window, 'touchstart', () => { this.A.touch++; this.A.input = 'touch'; }, { passive: true });
    }
    hookWheel() {
      if (this.wac) return;
      this.wac = new AbortController();
      window.addEventListener('wheel', (e) => {
        const A = this.A, now = performance.now(), gap = now - A.wheelAt;
        A.wheelAt = now; A.input = 'wheel'; A.dir = Math.sign(e.deltaY) || A.dir || 1;
        if (!this.pinned()) { A.hold = false; return; }
        if (A.busy || (A.hold && gap < 180 && now < A.holdUntil)) { e.preventDefault(); return; }   // still the gesture that started the slide
        if (A.locked && e.deltaY > 0 && (A.step === 0 || A.step === A.last)) {   // the first and last screens are held for a moment, and until the running gesture has died away
          if (this.resting(now, gap < 180)) { e.preventDefault(); return; }
          A.locked = false;
        }
        A.hold = false;
      }, { passive: false, signal: this.wac.signal });
    }
    unhookWheel() { if (this.wac) { this.wac.abort(); this.wac = null; } }
    resting(now, moving) { return now < this.A.restUntil || (moving && now < this.A.restUntil + 2500); }   // the gesture rule gives up after a while, so an unbroken scroll is never stuck
    measure() { this.fit(); }
    fit() {
      if (MD.design || reduce.matches) return;
      this.classList.remove('is-flat');
      if (this.classList.contains('md-slides') && matchMedia('(max-width: 767.98px)').matches) { this.classList.add('is-flat'); this.light(this.A.step); return; }   // phones: one row you slide sideways
      const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--md-barh')) || 72;
      const head = this.querySelector('.md-slides__head');
      this.style.setProperty('--md-headh', (head ? head.offsetHeight : 0) + 'px');   // the panels start under the heading
      this.sizePics();
      const room = this.stage.clientHeight - bar - 24 - (head ? head.offsetHeight + 24 : 0);
      this.classList.toggle('is-flat', [...this.querySelectorAll('.md-about__in')].some((n) => n.offsetHeight > room));
      this.light(this.A.step);   // stacked panels are all live; pinned ones hide the slides that are off screen
    }
    // the slide in view is live; the others sit off screen, so they are hidden from the keyboard and screen readers (a Tab
    // would otherwise land on a link nobody can see); stacked (flat) panels are all live
    // two per screen: the tallest photo that still leaves room for the words under it (both photos get the same size);
    // the words wrap to the column, which follows the photo, so it is measured twice
    sizePics() {
      const panel = this.querySelector('.md-slides__panel'), ins = panel && panel.querySelector('.md-slides__in--2');
      if (!ins) return;
      const beside = getComputedStyle(ins).getPropertyValue('--md-beside').trim() === '1';   // words beside the photo, or under it
      for (let pass = 0; pass < (beside ? 1 : 2); pass++) {
        const pcs = getComputedStyle(panel), room = this.stage.clientHeight - parseFloat(pcs.paddingTop) - parseFloat(pcs.paddingBottom);
        const num = ins.querySelector('.md-slides__num'), numH = num ? num.offsetHeight + 12 : 0;
        const textH = beside ? 0 : Math.max(0, ...[...this.querySelectorAll('.md-slides__cell .md-slides__text')].map((t) => t.offsetHeight)) + 16;
        this.style.setProperty('--md-pich', Math.max(160, Math.floor(room - numH - textH)) + 'px');
      }
    }
    light(step) {
      const flat = this.classList.contains('is-flat');
      this.panels.forEach((p, i) => {
        const on = i === step; p.classList.toggle('is-on', on);
        if (on || flat) { p.removeAttribute('inert'); p.removeAttribute('aria-hidden'); } else { p.setAttribute('inert', ''); p.setAttribute('aria-hidden', 'true'); }
      });
    }
    set(step, animate) {
      const A = this.A;
      if (A.step === step) return;
      A.step = step; A.acc = 0; this.light(step);
      if (!animate) { this.classList.add('is-now'); this.style.setProperty('--as', step); void this.offsetWidth; this.classList.remove('is-now'); return; }
      A.busy = true; A.hold = true; A.used = A.touch; A.holdUntil = performance.now() + A.ms + 900;   // the rest of the gesture that started the slide is absorbed (a long flick would otherwise take two)
      A.locked = step === A.last; A.restUntil = A.locked ? performance.now() + A.ms + 1200 : 0;   // locked for 1.2s once the last screen has landed
      // on a wheel the page is parked at this slide's own spot inside the pinned run (the stage is sticky, so nothing moves on
      // screen); a hard flick would otherwise eat the room meant for the later slides and the last one could unpin before it is held
      if (this.pinned()) this.park(step);
      this.style.setProperty('--as', step);
      this.timers.push(setTimeout(() => { A.busy = false; this.frame(); }, A.ms));
    }
    pinned() { const r = this.pin.getBoundingClientRect(); return r.top <= 2 && r.bottom >= innerHeight - 2; }
    park(step) { const at = this.anchor(step); if (Math.abs(scrollY - at) > 1) scrollTo({ top: at, left: 0, behavior: 'instant' }); this.A.y = at; }
    anchor(step) {   // scroll position for a slide: the run above the hold is shared equally between the slide changes
      const r = this.pin.getBoundingClientRect(), hold = (parseFloat(this.style.getPropertyValue('--md-hold')) || 40) / 100 * innerHeight;
      return Math.round(r.top + scrollY + step * Math.max(0, r.height - this.stage.offsetHeight - hold) / (this.A.last || 1));
    }
    frame() {
      const A = this.A, dy = scrollY - A.y;
      A.y = scrollY;
      if (this.classList.contains('is-flat')) return;
      const r = this.pin.getBoundingClientRect(), vh = innerHeight, now = performance.now(), gap = now - A.wheelAt, pinned = this.pinned();
      // arriving from above on a wheel, the first screen is held for a moment (and until the gesture that brought the page
      // here has died away), otherwise the flick that arrives scrolls straight through it
      const touch = A.input === 'touch';
      if (touch && dy) A.dir = Math.sign(dy);
      if (pinned && !A.in && dy > 0 && A.step === 0) { A.locked = true; A.restUntil = now + 600; A.dir = 1; if (touch) { A.hold = true; A.holdUntil = now + 600; A.used = A.touch; } this.park(0); }
      A.in = pinned;
      // only a new gesture counts towards the next slide: a fresh touch, or a wheel gesture that began after the last died away
      const fresh = touch ? A.touch !== A.used : !A.hold || gap > 180 || now >= A.holdUntil;
      // while a slide runs, while the gesture that started it goes on (on touch: while its momentum still moves the page), and
      // while the first or last screen is locked, the page is put back at the slide's spot every frame: Safari lets a flick's
      // momentum through preventDefault, a swipe's momentum would otherwise eat the room meant for the next slides, and the
      // stage is sticky so nothing moves on screen
      const moving = touch ? A.touch === A.used && Math.abs(dy) > 0.5 : gap < 180;   // on touch only the spent swipe's own momentum counts; a new swipe is a new intent
      const fenced = pinned && (A.busy || (A.hold && moving && now < A.holdUntil) || (A.locked && A.dir > 0 && (A.step === 0 || A.step === A.last) && this.resting(now, moving)));
      if (fenced) this.park(A.step);
      if (A.busy || !fresh || fenced) A.acc = 0;
      else { if (dy && Math.sign(dy) !== Math.sign(A.acc)) A.acc = 0; A.acc += dy; }
      if (A.busy) return;
      if (r.top > vh) { this.set(0, false); return; }           // not reached yet: waiting on the title
      if (r.bottom < 0) { this.set(A.last, false); return; }    // scrolled past: the facts are showing
      if (pinned) {
        if (A.acc > 24 && A.step < A.last && r.top < -2) this.set(A.step + 1, true);
        else if (A.acc < -24 && A.step > 0) this.set(A.step - 1, true);
      } else if (r.top > 2 && A.step > 0 && dy < 0) this.set(0, true);                  // came back up past the start in one go
      else if (r.bottom < vh - 2 && A.step < A.last && dy > 0) this.set(A.last, true);  // went down past the end in one go
    }
    teardown() { this.unhookWheel(); }
  }
  MD.define('md-about', About);
})();
